import { z } from 'zod';

// ── ToolTarget ────────────────────────────────────────────────────────────────
export const VALID_TOOL_TARGETS = [
  'internet_search',
  'knowledge_search',
  'image_gen',
  'code_exec',
  'run_query',
  'get_weather',
  '*',
] as const;
export type ToolTarget = typeof VALID_TOOL_TARGETS[number] | string;

// ── Visibility ────────────────────────────────────────────────────────────────
export const templateVisibilitySchema = z.enum(['private', 'workspace', 'public']);
export type TemplateVisibility = z.infer<typeof templateVisibilitySchema>;

// ── TemplateSource (resolution output) ───────────────────────────────────────
export type TemplateSource = 'user' | 'system' | 'builtin';

// ── UserTemplate (full, from API) ────────────────────────────────────────────
export const userTemplateSchema = z.object({
  id:            z.string().uuid(),
  user_id:       z.string(),
  workspace_id:  z.string(),
  name:          z.string(),
  description:   z.string().nullable().optional(),
  is_active:     z.boolean(),
  is_default:    z.boolean(),
  tool_target:   z.string().nullable().optional(),   // null = universal
  schema_hint:   z.string().nullable().optional(),
  template_html: z.string(),
  template_css:  z.string().nullable().optional(),
  template_js:   z.string().nullable().optional(),
  preview_data:  z.record(z.unknown()).nullable().optional(),
  version:       z.number(),
  parent_id:     z.string().uuid().nullable().optional(),
  visibility:    templateVisibilitySchema,
  created_at:    z.string(),
  updated_at:    z.string(),
}).strict();
export type UserTemplate = z.infer<typeof userTemplateSchema>;

// ── SystemTemplate (read-only, from /api/system/templates) ───────────────────
export const systemTemplateSchema = z.object({
  id:            z.string(),
  tool_target:   z.string(),    // "*" = universal
  name:          z.string(),
  description:   z.string().nullable().optional(),
  template_html: z.string(),
  template_css:  z.string().nullable().optional(),
  template_js:   z.string().nullable().optional(),
  preview_data:  z.record(z.unknown()).nullable().optional(),
  version:       z.string(),
}).strict();
export type SystemTemplate = z.infer<typeof systemTemplateSchema>;

// ── ResolvedTemplate (output of GET /api/user/templates/resolve) ──────────────
export const resolvedTemplateSchema = z.object({
  template_id:   z.string().uuid().nullable().optional(),
  source:        z.enum(['user', 'system', 'builtin']),
  name:          z.string(),
  tool_target:   z.string(),
  template_html: z.string(),
  template_css:  z.string().nullable().optional(),
  template_js:   z.string().nullable().optional(),
}).strict();
export type ResolvedTemplate = z.infer<typeof resolvedTemplateSchema>;

// ── Compiled Template (engine output) ─────────────────────────────────────────
export interface CompiledTemplate {
  html:    string;        // rendered HTML (Handlebars compiled + data applied)
  css:     string | null; // scoped CSS (with [data-tr-scope] prefix)
  scopeId: string;        // unique ID for CSS scoping attribute
}

// ── List Response ─────────────────────────────────────────────────────────────
export const userTemplateListResponseSchema = z.object({
  templates: z.array(userTemplateSchema),
  total:     z.number(),
  page:      z.number(),
  limit:     z.number(),
}).strict();
export type UserTemplateListResponse = z.infer<typeof userTemplateListResponseSchema>;

export const systemTemplateListResponseSchema = z.object({
  templates: z.array(systemTemplateSchema),
}).strict();
export type SystemTemplateListResponse = z.infer<typeof systemTemplateListResponseSchema>;

// ── Create/Update DTOs ─────────────────────────────────────────────────────────
export interface CreateTemplateDTO {
  name:          string;
  description?:  string;
  tool_target?:  string | null;
  schema_hint?:  string | null;
  template_html: string;
  template_css?: string | null;
  template_js?:  string | null;
  preview_data?: Record<string, unknown> | null;
  is_default?:   boolean;
  visibility?:   TemplateVisibility;
}

export type UpdateTemplateDTO = Partial<CreateTemplateDTO>;

// ── Enterprise Template ─────────────────────────────────────────────────────
export interface EnterpriseTemplate {
  id:            string;
  enterprise_id: string;
  name:          string;
  description?:  string;
  tool_target?:  string;
  schema_hint?:  string;
  template_html: string;
  template_css?: string;
  template_js?:  string;
  preview_data?: Record<string, unknown>;
  is_active:     boolean;
  is_default:    boolean;
  version:       number;
  created_by:    string;
  created_at:    string;
  updated_at:    string;
}

export interface CreateEnterpriseTemplateDTO {
  name:          string;
  description?:  string;
  tool_target?:  string | null;
  schema_hint?:  string | null;
  template_html: string;
  template_css?: string | null;
  template_js?:  string | null;
  preview_data?: Record<string, unknown> | null;
  is_default?:   boolean;
}

// ── Product (Workspace) Template ────────────────────────────────────────────
export interface ProductTemplate {
  id:           string;
  workspace_id: string;
  enterprise_id:string;
  name:         string;
  description?: string;
  tool_target?: string;
  schema_hint?: string;
  template_html:string;
  template_css?: string;
  template_js?:  string;
  preview_data?: Record<string, unknown>;
  is_active:    boolean;
  is_default:   boolean;
  version:      number;
  created_by:   string;
  created_at:   string;
  updated_at:   string;
}

export interface CreateProductTemplateDTO {
  name:          string;
  description?:  string;
  tool_target?:  string | null;
  schema_hint?:  string | null;
  template_html: string;
  template_css?: string | null;
  template_js?:  string | null;
  preview_data?: Record<string, unknown> | null;
  is_default?:   boolean;
}

// ── Tool call tracker state (for streaming display) ────────────────────────────
export type ToolCallStatus = 'running' | 'done' | 'error';

export interface ToolCallState {
  id:          string;
  name:        string;
  status:      ToolCallStatus;
  args?:       Record<string, unknown>;
  result?:     unknown;
  schemaHint?: string;
  resultCount?: number;
  error?:      string;
  startedAt:   number;
  completedAt?: number;
}
